import { useMutation, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toastr from 'react-hot-toast';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { QueryOptions, SPARK_RESOURCE_TYPES } from 'constants/common';
import type {
  SinglePersonalOverviewResponse,
  PersonalOverviewAttribute,
} from 'types/PersonalOverview/personalOverview';

type MutationVariables = Partial<PersonalOverviewAttribute>;

export const useUpdatePersonalOverview = (
  personalOverviewID: string | undefined,
  options?: QueryOptions
) => {
  const queryClient = useQueryClient();

  const updatePersonalOverview = (mutationVariables: MutationVariables) => {
    if (!personalOverviewID) {
      return Promise.reject('Personal overview ID cannot be null');
    }

    const body = {
      data: {
        id: personalOverviewID,
        type: SPARK_RESOURCE_TYPES.PERSONAL_OVERVIEWS,
        attributes: mutationVariables,
      },
    };

    const url = generateURL(
      SPARK_RESOURCE_TYPES.PERSONAL_OVERVIEWS,
      [],
      personalOverviewID
    );

    return fetchWrapper<SinglePersonalOverviewResponse>({
      url,
      method: 'PATCH',
      body,
    });
  };

  return useMutation('updatePersonalOverview', updatePersonalOverview, {
    onSuccess: async () => {
      if (options?.shouldInvalidatedOnSuccess) {
        await queryClient.invalidateQueries([
          'personalOverview',
          personalOverviewID,
        ]);
      }
    },
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`Failed to update personal overview: ${error.message}`);
      }
    },
  });
};
